import { ImageEditorModal } from '@/components/modals/ImageEditorModal/ImageEditorModal';
import { useSubmitPartImageSearch } from '@/hooks/api/part-finder-image-search/useSubmitPartImageSearch';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useToast } from '@/hooks/useToast';
import { Backdrop, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { convertHeicPhotos } from './convertHeicPhotos';
import { ImagePartFinderModal } from './ImagePartFinderModal';
import { ACCEPTED_PART_IMAGE_EXTENSIONS, addPartImageFiles, isHeifFile } from './partImageFiles';

const MAX_CONTEXT_LENGTH = 1000;

interface HeicConversionProgress {
  photoNumber: number;
  total: number;
}

const conversionMessage = ({ photoNumber, total }: HeicConversionProgress): string =>
  total > 1 ? `Converting HEIC photo ${photoNumber} of ${total}…` : 'Converting HEIC photo…';

const unconvertedMessage = (names: string[]): string =>
  `${names.join(', ')} couldn't be converted in this browser. Export ${names.length > 1 ? 'them' : 'it'} as JPEG and try again.`;

export interface FindFromImagesProperties {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

export const FindFromImages = ({ fileInputRef }: FindFromImagesProperties) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submittedImages, setSubmittedImages] = useState<File[]>();
  const [context, setContext] = useState('');
  const [conversion, setConversion] = useState<HeicConversionProgress>();
  const { isImageSearchCroppingDisabled } = useFeatureFlags();
  const { showToast } = useToast();
  const submit = useSubmitPartImageSearch();

  const identify = (images: File[], partContext?: string) => {
    setPendingFiles([]);
    setSubmittedImages(images);
    submit.mutate({ clientRequestId: crypto.randomUUID(), images, context: partContext?.trim() || undefined });
  };

  const convertForBrowser = async (files: File[]): Promise<File[]> => {
    const total = files.filter(file => isHeifFile(file)).length;
    const { photos, unconverted } = await convertHeicPhotos(files, photoNumber =>
      setConversion({ photoNumber, total })
    );
    setConversion(undefined);
    if (unconverted.length > 0) {
      showToast({ message: unconvertedMessage(unconverted), type: 'warning' });
    }
    return photos;
  };

  const startWith = async (picked: File[]) => {
    const { files, problem } = addPartImageFiles([], picked);
    if (problem) {
      showToast({ message: problem, type: 'warning' });
    }
    if (files.length === 0) {
      return;
    }
    setSubmittedImages(undefined);
    submit.reset();
    setContext('');
    const photos = await convertForBrowser(files);
    if (photos.length === 0) {
      return;
    }
    if (isImageSearchCroppingDisabled) {
      identify(photos);
    } else {
      setPendingFiles(photos);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void startWith([...(event.target.files ?? [])]);
    event.target.value = '';
  };

  const close = () => {
    setSubmittedImages(undefined);
    submit.reset();
  };

  return (
    <>
      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept={ACCEPTED_PART_IMAGE_EXTENSIONS}
        multiple
        style={{ display: 'none' }}
        data-testid='part-image-input'
      />
      <Backdrop
        open={conversion !== undefined}
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
        data-testid='part-image-converting'
      >
        <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderRadius: '12px' }}>
          <CircularProgress size={20} />
          <Typography variant='body2'>{conversion && conversionMessage(conversion)}</Typography>
        </Paper>
      </Backdrop>
      {pendingFiles.length > 0 && (
        <ImageEditorModal
          open
          files={pendingFiles}
          submitLabel='Identify'
          onComplete={editedFiles => identify(editedFiles, context)}
          onCancel={() => setPendingFiles([])}
        >
          <TextField
            label='Context (optional)'
            placeholder='e.g., off an M1A2 hydraulic line'
            value={context}
            onChange={event => setContext(event.target.value.slice(0, MAX_CONTEXT_LENGTH))}
            fullWidth
            helperText='Include any label, nameplate, or stamping in the photos.'
            slotProps={{ htmlInput: { 'data-testid': 'part-image-context' } }}
          />
        </ImageEditorModal>
      )}
      {submittedImages && (
        <ImagePartFinderModal
          images={submittedImages}
          identificationId={submit.data?.id}
          isSubmitting={submit.isPending}
          submitError={submit.error ?? undefined}
          onTryOtherPhotos={() => fileInputRef.current?.click()}
          onClose={close}
        />
      )}
    </>
  );
};
